import React, { useEffect, useState } from 'react';
import useAxios from "../../hooks/useAxios";
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import Error from '../Error/Error';
import Loading from '../Loading/Loading';
import './PieChart.css';
import { useService } from '../../context/ServiceProvider';
import { useTheme } from '../../context/ThemeProvider';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../../config/authConfig';

interface Props {
    type: string;
    url: string;
    colors?: string[];
}

interface ApiResponse {
    data: number[];
    labels: string[];
    detailsApiCall: string[];
}

const PieChart: React.FC<Props> = ({ type, url, colors = [] }) => {
    const { currentService } = useService();
    const { theme } = useTheme();
    const { instance, accounts } = useMsal();
    const navigate = useNavigate();

    const [titleColor, setTitleColor] = useState('');
    const [token, setToken] = useState<string | null>(null);

    const { response, error, loading, fetchData } = useAxios(currentService?.apiBaseUrl || "", token ?? undefined);

    const getCSSVar = (name: string) =>
        getComputedStyle(document.documentElement).getPropertyValue(name).trim();

    const title = `${type} Chart`;

    useEffect(() => {
        const getToken = async () => {
            if (accounts.length === 0) return;

            try {
                const result = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0],
                });
                setToken(result.accessToken);
            } catch (err) {
                console.error("Failed to acquire token silently:", err);
            }
        };

        getToken();
    }, [accounts, instance]);

    useEffect(() => {
        if (!token) return;
        fetchData({ url, method: "GET" });
    }, [url, token, fetchData]);

    // Update chart title color based on theme
    useEffect(() => {
        requestAnimationFrame(() => {
            setTitleColor(getCSSVar('--title-color'));
        });
    }, [theme]);

    const apiResponse = response as ApiResponse | null;

    if (loading) return <Loading label={title} />;
    if (error) return <Error error={error} />;

    const chartOption = apiResponse && apiResponse.data && apiResponse.labels
        ? {
            title: {
                text: title,
                left: 'center',
                top: 10,
                textStyle: {
                    fontSize: 16,
                    color: titleColor,
                }
            },
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)'
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                bottom: 0,
                data: apiResponse.labels,
                textStyle: {
                    fontSize: 12,
                    color: titleColor,
                    overflow: 'truncate',
                },
                padding: [10, 0, 10, 0],
                itemGap: 10,
                itemWidth: 14,
                itemHeight: 14,
                pageIconSize: 12,
                pageButtonItemGap: 4,
            },
            series: [
                {
                    name: title,
                    type: 'pie',
                    radius: '75%',
                    center: ['50%', '50%'],
                    data: apiResponse.data.map((value, index) => ({
                        value,
                        name: apiResponse.labels[index] || `Label ${index}`,
                        apiUrl: apiResponse.detailsApiCall[index] || '',
                        itemStyle: {
                            color: colors[index] || undefined,
                        },
                    })),
                    label: { show: false },
                    labelLine: { show: false },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.5)',
                        },
                    },
                },
            ],
        }
        : null;

    type EChartsClickEvent = {
        name: string;
        data?: {
            apiUrl?: string;
        };
    };

    return (
        <div className="pie-chart-container">
            {chartOption ? (
                <ReactECharts
                    option={chartOption}
                    style={{ height: '350px', width: '100%', maxWidth: '320px' }}
                    onEvents={{
                        click: (params: EChartsClickEvent) => {
                            navigate(`/detail/${type}/${encodeURIComponent(params.name)}`, {
                                state: {
                                    type: type,
                                    label: params.name,
                                    apiUrl: params.data?.apiUrl,
                                },
                            });
                        },
                    }}
                />
            ) : (
                <p>No data available for {title}</p>
            )}
        </div>
    );
};

export default PieChart;
